import { EventEmitter, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { DefaultUrlSerializer, Router, UrlTree } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserRole } from '../../shared/enum/user-role.enum';
import { CartInfo } from '../../shared/models/cart-info.model';
import { RoutePath } from '../../shared/models/route-path.enum';
import { SharedConfig } from '../../shared/models/shared-config.model';
import { Shop } from '../../shared/models/shop.model';
import { sharedConfig } from '../../shared/shared.config';
import { AuthService } from '../auth/auth.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { amwaysite, BUSINESS_NATURE, inactive, selectedLangKey } from './../../shared/constants/constants';
import { cartInfoKey, isSfFlowKey, sharedUrlKey, shopInfoKey } from './../../shared/constants/storage-key-constants';

@Injectable({
  providedIn: 'root'
})
export class UiHelperService extends DefaultUrlSerializer {
  selectedLanguage: string;
  selectedLang$: Observable<string>;
  roleChange$: Observable<boolean>;
  translationChangeEmitter = new EventEmitter();
  translationData: any;
  private roleChnageSubject = new Subject<boolean>();
  private renderer: Renderer2;
  private selectedLangSubject$ = new Subject<string>();

  /**
   * Creates an instance of ui helper service.
   *
   * @param router instance of Router
   * @param translateService instance of TranslateService
   * @param localStorageService instance of LocalStorageService
   * @param sessionStorageService instance of SessionStorageService
   * @param rendererFactory instance of RendererFactory2
   * @param config instance of SharedConfig
   */
  constructor(
    private router: Router,
    private translateService: TranslateService,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private rendererFactory: RendererFactory2,
    private titleService: Title,
    @Inject(sharedConfig) public config: SharedConfig
  ) {
    super();
    this.translateService.onLangChange.pipe(take(1)).subscribe((event: LangChangeEvent) => {
      this.selectedLanguage = this.getSelectedLanguage();
      if (this.selectedLanguage !== event.lang) {
        this.setTranslationConfigs();
      } else {
        this.translationData = event.translations;
      }
    });
    this.roleChange$ = this.roleChnageSubject.asObservable();
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.selectedLang$ = this.selectedLangSubject$.asObservable();
  }

  /**
   * Extended method of DefaultUrlSerializer
   * used to parse give url
   *
   * @param string url
   * @returns UrlTree
   */
  parse(url: string): UrlTree {
    return super.parse(url);
  }

  /**
   * Extended method of DefaultUrlSerializer
   * used to serialize given urlTree retrived from parse method
   *
   * @param UrlTree tree structure of url
   * @returns string serialized tree stucture of UrlTree
   */
  serialize(tree: UrlTree): string {
    return super.serialize(tree);
  }

  /**
   * @method isModalClassApplied
   * @description to check if modal-show class is already applied
   *  used for nested modals so that the class is removed by the outermost modal only
   * @return boolean modal is applied or not
   */
  isModalClassApplied(): boolean {
    return document.getElementsByTagName('body')[0].classList.contains('modal-show'); // TODO - use element ref to get the body
  }

  /**
   * @method {onInitPopup}
   * @description method used to apply modal class on body to prevent
   * overflow issue in case of popup in mobile view.
   * method needs to be called on popup initialization.
   * @return void
   */
  onInitPopup(): void {
    this.renderer.addClass(document.body, 'modal-show');
  }

  /**
   * @method onClosePopup
   * @description method used to remove modal class from body which
   * get applied to prevent overflow issue in case of popup in mobile view
   * method needs to be called on popup close.
   * @return void
   */
  onClosePopup(): void {
    this.renderer.removeClass(document.body, 'modal-show');
  }

  /**
   * @method roleUpdate
   * @description method used to emit role event whenever
   * role gets updated in the application
   * @return void
   * @param boolean loaded
   */
  roleUpdate(loaded: boolean): void {
    this.roleChnageSubject.next(loaded);
  }

  /**
   * @method setTranslationConfigs
   * @description This method is used to set initial translation config
   * @return void
   */
  setTranslationConfigs(): void {
    this.translateService.use(this.selectedLanguage)
      .pipe(take(1)).subscribe(response => {
        this.translationData = response;
      });
  }

  /**
   * @method useLanguage
   * @description this method is used to reset translation with selected language
   * @param string newSelectedLanguage
   * @return void
   */
  useLanguage(newSelectedLanguage: string): void {
    if (this.selectedLanguage !== newSelectedLanguage) {
      this.selectedLanguage = newSelectedLanguage;
      this.localStorageService.setItem(selectedLangKey, this.selectedLanguage);
      this.selectedLangSubject$.next(this.selectedLanguage);
      this.translateService.use(this.selectedLanguage);
      this.translateService.getTranslation(this.selectedLanguage)
        .pipe(take(1)).subscribe(response => {
          this.translationData = response;
          this.refreshPage();
          this.translationChangeEmitter.emit();
        });
    }
  }

  /**
   * @method getSelectedLanguage
   * @description This method is used to get selected language from storage
   * @returns string selected language
   */
  getSelectedLanguage(): string {
    const selectedLanguage: string = this.localStorageService.getItem(selectedLangKey);
    if (selectedLanguage) {
      return selectedLanguage;
    } else {
      this.localStorageService.setItem(selectedLangKey, this.config.defaultLanguage);
      this.selectedLangSubject$.next(this.config.defaultLanguage);
      return this.config.defaultLanguage;
    }
  }

  /**
   * @method getTranslation
   * @description Function to function translation value as per the key
   * @param string key
   * @param string label
   * @returns string tarnslated value
   */
  getTranslation(key: string, label: string): string {
    return this.translationData && this.translationData[key] && this.translationData[key][label] ?
      this.translationData[key][label] : key + '.' + label;
  }

  /**
   * @method navigateToPath
   * @description Navigate to the passed path alongwith shopUid from session
   * @param string path : route path without slash(/) as prefix
   * @return void
   */
  navigateToPath(path: string): void {
    const shop: Shop = this.sessionStorageService.getItem(shopInfoKey) || <Shop>{};
    path = path ? path.replace(/^\/+|\/+$/g, '') : ''; // replace all leading slashes
    path = path ? `/${path}` : ''; // append the slash if path exists
    this.router.navigateByUrl(`${shop.shopUid}${path}`);
  }

  /**
   * @method refreshPage
   * @description Function is used to refresh the same page route without change the URI
   * @param string [url]
   * @return void
   */
  refreshPage(url?: string): void {
    const parsedurl = url ? url : this.parse(decodeURI(this.router.url));
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigateByUrl(parsedurl);
    this.router.onSameUrlNavigation = 'ignore';
  }

  /**
   * @method setPageTitle
   * @description Function is used to set application title.
   * @param string title : title to be set
   * @return void
   */
  setAppTitle(title = 'MyList | ' + amwaysite): void {
    this.titleService.setTitle(title);
  }

  /**
   * @method setShopnameAppTitle
   * @description Function is used to set shopname in application title.
   * @return void
   */
  setShopnameAppTitle(): void {
    const shop = <Shop>this.sessionStorageService.getItem(shopInfoKey);
    const title = this.titleService.getTitle();
    let newtitle = 'MyList | ' + amwaysite;
    if (shop && shop.shopName) {
      newtitle = shop.shopName + ' | ' + amwaysite;
    }
    if (title !== newtitle) {
      this.titleService.setTitle(newtitle);
    }
  }

  /**
   * @name navigateToSharedUrl
   * @description Function to redirect to shared url
   * @memberof UiHelperService
   */
  navigateToSharedUrl() {
    const shopInfo = <Shop>this.sessionStorageService.getItem(shopInfoKey);
    const sharedUrl: string = this.sessionStorageService.getItem(sharedUrlKey);
    const isSfFlow = <boolean>this.sessionStorageService.getItem(isSfFlowKey);
    const session = this.sessionStorageService.getSessionInfo();
    this.sessionStorageService.clear();
    this.sessionStorageService.setItem(shopInfoKey, shopInfo);
    this.sessionStorageService.setSessionInfo(session);
    if (isSfFlow) {
      window.location.href = environment.hybrisSfBasepath;
    }
    if (sharedUrl && sharedUrl.length) {
      this.sessionStorageService.setItem(sharedUrlKey, sharedUrl);
    }
    if (this.authService.isRoleAvailable(UserRole.CUSTOMER)) {
      if (sharedUrl) {
        const urltree = this.parse(sharedUrl);
        this.router.navigateByUrl(urltree);
      } else if (shopInfo && shopInfo.shopUid) {
        this.navigateToPath(shopInfo && shopInfo.shoplistUid ? shopInfo.shoplistUid : '');
      } else {
        this.router.navigateByUrl(RoutePath.NO_CONTENT);
      }
    } else if (shopInfo && shopInfo.shopUid) {
      this.router.navigateByUrl(shopInfo.shopUid);
    } else {
      this.router.navigateByUrl(RoutePath.NO_CONTENT);
    }
  }

  /**
   * @description to copy the value to clipboard
   * @param value {string}
   */
  copyValue(value: string) {
    const element = <HTMLTextAreaElement>(document.createElement('textarea'));
    element.value = value;
    document.body.appendChild(element);
    element.select();
    element.setSelectionRange(0, (value && value.length > 0) ? value.length : 9999);
    document.execCommand('copy');
    document.body.removeChild(element);
  }

  /**
   *
   *
   * @description to check if cart already exists
   * @returns
   * @memberof UiHelperService
   */
  doesCartExist(): boolean {
    const cartInfo: CartInfo = this.sessionStorageService.getItem(cartInfoKey);
    return (cartInfo && cartInfo.cartId) ? true : false;
  }

  /**
   * @name getCartRoutePath
   * @description get route path for navigating to cart page;if user is logged-in, then navigate to cart via idp
   * @returns string
   * @memberof UiHelperService
   */
  getRoutePathForCartPage(): string {
    let path = '';
    const jwtToken = this.authService.getActiveJwtToken();
    if (jwtToken && jwtToken.businessNature &&
      (jwtToken.businessNature === BUSINESS_NATURE.abo
        || jwtToken.businessNature === BUSINESS_NATURE.member
        || jwtToken.businessNature === BUSINESS_NATURE.foa)
      && jwtToken.accountStatus !== inactive) {
      path = RoutePath.AUTH_OCC;
    } else {
      path = RoutePath.CART;
    }
    return path;
  }

  /**
   * @name navigateToLandingPage
   * @description Helper function to navigate user to SF or shop landing page
   * @memberof UiHelperService
   */
  navigateToLandingPage(refreshRequired?: boolean) {
    const shopInfo = <Shop>this.sessionStorageService.getItem(shopInfoKey);
    const isSfFlow = <boolean>this.sessionStorageService.getItem(isSfFlowKey);
    if (isSfFlow) {
      const sessionId: string = this.sessionStorageService.getSessionInfo();
      this.sessionStorageService.clear();
      this.sessionStorageService.setSessionInfo(sessionId);
      window.location.href = environment.hybrisSfBasepath;
    } else if (refreshRequired) {
      window.location.href = window.location.origin + `/mylist/${shopInfo.shopUid}`;
    } else {
      this.router.navigateByUrl(`${shopInfo.shopUid}`);
    }
  }

  /**
   * @name checkIfMobileDevice
   * @description Helper function to check if using mobile device
   * @memberof UiHelperService
   */
  checkIfMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) ||
      (/Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.platform))) {
      return true;
    }
    return false;
  }
}
